import { Capacitor } from '@capacitor/core';
import {
  checkNotificationPermission,
  openNotificationSettings,
  requestNotificationPermission
} from '@/services/push/notificationPermission';
import { ensurePushAvailability, getBootCount, getPushStore, requestPushSync } from '@/services/push/pushRegistration';
import { askPushPermission } from '@/services/push/pushSheets';
import { shouldAskPermission } from '@/services/push/pushTarget';

// Pedido de permissão CONTEXTUAL (plano, seção 8 "Telas"): o prompt do SO só
// aparece depois que o aluno entende o porquê — logo após postar um comentário
// ("quer ser avisado quando responderem?") ou concluir a 1ª aula. Nunca no 1º
// boot, nunca de novo depois de um "Agora não", uma vez por gatilho.
//
// Na 1ª entrada na área logada (sc-6255) a sheet também aparece, uma vez por
// aparelho e já no 1º boot: logo depois do login ou, para quem já estava logado
// e atualizou o app, na 1ª abertura da versão nova. Quem só assiste nunca
// comentava nem concluía aula e ficava sem aviso nenhum. O "Agora não" desse
// gatilho só fecha a sheet: o prompt do SO não foi gasto, e os gatilhos de
// comentário e aula continuam valendo.
//
// O que foi mostrado/recusado fica no localStorage do aparelho (não no backend):
// é decisão de UX deste aparelho, e a preferência de verdade (o que receber)
// mora em Configurações.

export const PROMPT_TRIGGERS = { ENTRY: 'entry', COMMENT: 'comment', LESSON: 'lesson' };

const PROMPT_STATE_KEY = 'ead_push_prompt_v1';
// Espera depois de chegar na área logada: a home termina de montar antes da sheet.
const ENTRY_PROMPT_DELAY_MS = 1500;

let _asking = false;
// Uma tentativa do gatilho de entrada por execução do app; quem garante "uma vez
// por aparelho" é o `shown` gravado quando a sheet abre.
let _entryTried = false;

function readPromptState () {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROMPT_STATE_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writePromptState (patch) {
  const next = Object.assign({}, readPromptState(), patch);
  try { localStorage.setItem(PROMPT_STATE_KEY, JSON.stringify(next)); } catch (e) {}
  return next;
}

function markShown (trigger) {
  const state = readPromptState();
  const shown = Object.assign({}, state.shown, { [trigger]: Date.now() });
  writePromptState({ shown });
}

// Player em tela cheia (fullscreen nativo, o por-CSS do iOS ou o full-window do
// video.js): não é hora de abrir sheet por cima. Fica para o próximo gatilho.
function playerInFullscreen () {
  if (typeof document === 'undefined') return false;
  const body = document.body;
  return Boolean(document.fullscreenElement || document.webkitFullscreenElement ||
    (body && (body.classList.contains('player-fullscreen') || body.classList.contains('vjs-full-window'))));
}

export async function maybeAskPushPermission (trigger) {
  const store = getPushStore();
  if (!store || !Capacitor.isNativePlatform() || _asking) return;
  if (!store.state.user.currentUser || !store.state.user.currentUser.studentId) return;
  _asking = true;
  try {
    const available = await ensurePushAvailability();
    const permission = await checkNotificationPermission();
    const eligible = shouldAskPermission({
      trigger,
      isApp: true,
      available,
      bootCount: getBootCount(),
      promptState: readPromptState(),
      permission
    });
    if (!eligible || playerInFullscreen()) return;

    markShown(trigger);
    const denied = permission === 'denied';
    const choice = await askPushPermission({ trigger, denied });

    if (choice === 'decline') {
      if (trigger !== PROMPT_TRIGGERS.ENTRY) writePromptState({ declinedAt: Date.now() });
      return;
    }
    if (choice !== 'accept') return;

    if (denied) {
      // Volta dos Ajustes = `resume`, que relê a permissão e reenvia o registro.
      await openNotificationSettings();
      return;
    }
    const result = await requestNotificationPermission();
    store.commit('push/SET_PERMISSION', result);
    requestPushSync('permission-granted', { force: true });
  } catch (e) {
    console.warn('[push] pedido de permissão falhou:', e?.message || e);
  } finally {
    _asking = false;
  }
}

// Chamado pelo host <PushSheets /> quando a rota entra na área logada. A tela de
// login mora no mesmo layout do host, então ele não remonta depois do login: é a
// navegação que avisa.
export function onEnteredAuthArea () {
  if (_entryTried) return;
  _entryTried = true;
  setTimeout(() => maybeAskPushPermission(PROMPT_TRIGGERS.ENTRY), ENTRY_PROMPT_DELAY_MS);
}
